"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { trackEvent } from "@/lib/analytics";
import {
  bannerHeading,
  browseJobsLabel,
  ctaEvent,
  ctaHref,
  type CtaRole,
} from "./content-loop-cta-copy";

type ContentLoopCtaProps = {
  /** Attribution slug, e.g. "guide:ibiza", "festival:glastonbury-jobs". */
  source: string;
  /** Destination/festival name for headings + interpolation. Omit for topic guides. */
  context?: string;
  /** "banner" = full dual-path block; "compact" = slim sidebar nudge. */
  variant?: "banner" | "compact";
  /** Heading override (e.g. a guide's existing "Ready for a season in Ibiza?"). */
  heading?: ReactNode;
  /**
   * Existing browse-jobs link. Shown as a tertiary link on the banner, and
   * used as the post-signup destination for the candidate CTA.
   */
  jobsHref?: string;
};

export function ContentLoopCta({
  source,
  context,
  variant = "banner",
  heading,
  jobsHref,
}: ContentLoopCtaProps) {
  const candidateHref = ctaHref("candidate", source, jobsHref);
  const employerHref = ctaHref("employer", source);
  const track = (role: CtaRole) => () =>
    trackEvent("content_loop_cta", ctaEvent(role, source, context));

  if (variant === "compact") {
    return (
      <div className="bg-navy text-white rounded-[18px] p-7 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(28, 138, 153,0.1),transparent_70%)]"
          aria-hidden="true"
        />
        <h4 className="font-serif text-[20px] font-bold mb-2.5 relative z-10">
          {heading ?? bannerHeading(context)}
        </h4>
        <p className="text-[13px] text-white/70 mb-5 relative z-10">
          Message employers directly or get discovered. Free.
        </p>
        <Link
          href={candidateHref}
          onClick={track("candidate")}
          className="inline-block px-6 py-2.5 bg-gold text-white rounded-sm text-[13px] font-semibold no-underline transition-all hover:bg-gold-light hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(28, 138, 153,0.3)] relative z-10"
        >
          Create profile {"→"}
        </Link>
        <Link
          href={employerHref}
          onClick={track("employer")}
          className="block mt-4 text-[12.5px] text-white/60 no-underline hover:text-gold transition-colors relative z-10"
        >
          Hiring? Post a role {"→"}
        </Link>
      </div>
    );
  }

  return (
    <section className="bg-gradient-to-br from-gold/[0.06] to-gold/[0.02] border-[1.5px] border-gold/20 rounded-[20px] p-8 md:p-12 text-center">
      <h2 className="font-serif text-[28px] md:text-[32px] mb-6 text-navy">
        {heading ?? bannerHeading(context)}
      </h2>
      <div className="grid md:grid-cols-2 gap-4 max-w-[680px] mx-auto text-left">
        <div className="bg-white rounded-[16px] border border-border-light p-6 flex flex-col">
          <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-gold mb-2">
            Looking for work
          </div>
          <p className="text-[14px] text-text-muted mb-5 flex-1">
            Message employers directly or get discovered. No fees, no applications.
          </p>
          <Link
            href={candidateHref}
            onClick={track("candidate")}
            className="inline-flex items-center justify-center bg-gold text-white px-6 py-3 rounded-sm text-sm font-semibold no-underline transition-all hover:bg-gold-light hover:-translate-y-0.5"
          >
            Create profile {"→"}
          </Link>
        </div>
        <div className="bg-navy text-white rounded-[16px] p-6 flex flex-col relative overflow-hidden">
          <div
            className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(28, 138, 153,0.12),transparent_70%)]"
            aria-hidden="true"
          />
          <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-gold mb-2 relative z-10">
            Hiring staff
          </div>
          <p className="text-[14px] text-white/75 mb-5 flex-1 relative z-10">
            Reach seasonal workers ready for the season. Post a role, free.
          </p>
          <Link
            href={employerHref}
            onClick={track("employer")}
            className="inline-flex items-center justify-center bg-gold text-white px-6 py-3 rounded-sm text-sm font-semibold no-underline transition-all hover:bg-gold-light hover:-translate-y-0.5 relative z-10"
          >
            Post a role {"→"}
          </Link>
        </div>
      </div>
      {jobsHref && (
        <Link
          href={jobsHref}
          onClick={track("browse_jobs")}
          className="inline-block mt-6 text-[13px] font-medium text-text-muted no-underline hover:text-gold transition-colors"
        >
          {browseJobsLabel(context)}
        </Link>
      )}
    </section>
  );
}
